import { hasAlphaRoleAccess } from "../../hooks/useAlphaAccess";
import {
  ArrowUpRight,
  CircleAlert,
  LoaderCircle,
  RefreshCw,
  SlidersHorizontal,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import V3PeriodFilterRow from "../../components/V3PeriodFilterRow";
import {
  bucketFactorsToCumulativeSeries,
  getBucketMsForFilter,
  getV3DefaultFilter,
  getV3FilterWindowBounds,
  V3_FACTOR_SLOT_MS,
  V3_INVESTOR_TIME_FILTER_OPTIONS,
  type CumulativeSeriesPoint,
  type V3TimeFilter,
} from "../../components/performanceChartV3Utils";
import { DEFAULT_BENCHMARK_SYMBOL, getBenchmarkBySymbol } from "../../config/benchmarkContracts";
import { useAuth } from "../../contexts/AuthContext";
import {
  PLUTUS_MANAGED_LABEL,
  useAlphaAllocationDraftContext,
} from "../../contexts/AlphaAllocationDraftContext";
import { useAccountSummary } from "../../hooks/useAccountData";
import { ACTUAL_COMPARISON_ALL_KEY } from "../../hooks/useStrategyVsActualComparison";
import investorStrategyService, {
  getSignedDriftedWeight,
  getSignedWeight,
  type InvestorStrategyRecord,
} from "../../services/investorStrategyService";
import type { FactorEntry } from "../../services/productFactorsService";
import { productSearchService } from "../../services/productSearchService";
import type { InvestorAlphaRouteState } from "../../types/investorAlpha";
import {
  formatCurrencyWithCommas,
  formatPercentage,
  formatSignedPercentage,
} from "../../utils/numberUtils";
import { getManagedStrategySeedWeight } from "../../utils/strategyAllocationLimits";
import { computeStrategyTotals } from "../../utils/strategyTotals";
import DetailNetBenchmarkChart from "../dashboard/components/DetailNetBenchmarkChart";
import {
  loadAlphaManagedStrategyDataByProductId,
  type AlphaManagedStrategyPerformanceData,
} from "./alphaRecommendationData";
import type { AlphaDashboardPreviewPageProps } from "./alphaPageSubject";
import AdminTargetDashboardAdapter from "./AdminTargetDashboardAdapter";
import {
  getAlphaAdoptMixFromRouteState,
  hasAlphaAdoptMixRouteState,
  stripAlphaAdoptMixRouteState,
  type AlphaAdoptMixEntry,
} from "./alphaAnalyzerAdopt";
import { getAlphaSetupDestination } from "./alphaSetupDestination";
import AlphaPreferencesCard from "./AlphaPreferencesCard";
import AlphaAllocationsModal from "./AlphaAllocationsModal";
import AlphaWhyManagedTeaser from "./AlphaWhyManagedTeaser";
import { openAlphaRiskProfileUpdate } from "./alphaHistory";
import useAlphaStrategyActualPerformance from "./useAlphaStrategyActualPerformance";
import AlphaPerformanceChartContent from "./AlphaPerformanceChartContent";

type CustomerStatus = "new" | "existing";
type StrategyLoadStatus = "loading" | "loaded" | "failed";

const ALPHA_BENCHMARK = getBenchmarkBySymbol(DEFAULT_BENCHMARK_SYMBOL);
const CHART_FILTERS = V3_INVESTOR_TIME_FILTER_OPTIONS.map(
  (option) => option.value
) as V3TimeFilter[];

const emptyPeriodRecord = (value: boolean): Record<V3TimeFilter, boolean> =>
  Object.fromEntries(
    CHART_FILTERS.map((period) => [period, value])
  ) as Record<V3TimeFilter, boolean>;

function routeCustomerStatus(state: unknown): CustomerStatus | null {
  if (!state || typeof state !== "object" || Array.isArray(state)) return null;
  const status = (state as InvestorAlphaRouteState).customerStatus;
  return status === "new" || status === "existing" ? status : null;
}

function filterFactorsForPeriod(
  factors: FactorEntry[],
  period: V3TimeFilter,
  anchor: Date,
  inceptionMs: number
): FactorEntry[] {
  if (period === "All" || period === "Since inception") return factors;
  const { start, end } = getV3FilterWindowBounds(period, anchor);
  const startMs = start?.getTime() ?? inceptionMs;
  const endMs = end.getTime();
  if (startMs >= endMs) return [];
  return factors.filter((factor) => {
    const timestamp = new Date(factor.t).getTime();
    return timestamp > startMs && timestamp <= endMs;
  });
}

function getChartStartMs(
  data: AlphaManagedStrategyPerformanceData,
  period: V3TimeFilter,
  anchor: Date
): number {
  if (period === "All") {
    return data.window.requestedStartMs ?? data.window.startMs;
  }
  if (period === "Since inception") return data.window.startMs;
  return getV3FilterWindowBounds(period, anchor).start?.getTime()
    ?? data.window.startMs;
}

function buildPeriodSeries(
  data: AlphaManagedStrategyPerformanceData,
  period: V3TimeFilter,
  factors: FactorEntry[],
  anchor: Date
): CumulativeSeriesPoint[] {
  const filtered = filterFactorsForPeriod(
    factors,
    period,
    anchor,
    data.window.startMs
  );
  if (filtered.length === 0) return [];
  return bucketFactorsToCumulativeSeries(
    filtered,
    getChartStartMs(data, period, anchor),
    getBucketMsForFilter(period)
  );
}

// Resolved catalog metadata per allocation key. providerId is required to
// build the product detail route; the strategy record does not carry it.
type AllocationProduct = { title: string; providerId: number };
type AllocationProductMap = Record<string, AllocationProduct>;

// The lite dashboard always measures allocations against the whole portfolio, so
// cash is simply whatever is not allocated. Margin capacity and the lower
// retirement cap are deliberately out of scope here.
const ALPHA_ALLOCATION_BASIS_PCT = 100;

function allocationKey(entry: InvestorStrategyRecord): string {
  return `${entry.entityType}-${entry.productId}`;
}

// Drift is the live position size; the allocated weight is the best estimate
// until the backend has computed drift for a freshly provisioned allocation. A
// drifted weight of 0 is a real value and must not fall back.
function getCurrentWeight(entry: InvestorStrategyRecord): number {
  return getSignedDriftedWeight(entry) ?? getSignedWeight(entry);
}

function allocationLabel(
  entry: InvestorStrategyRecord,
  allocationProducts: AllocationProductMap,
  includeManagedProductId = false
): string {
  if (entry.entityType === "MANAGED_STRATEGY") {
    return includeManagedProductId
      ? `Plutus Managed #${entry.productId}`
      : "Plutus Managed";
  }
  const resolvedName = allocationProducts[allocationKey(entry)]?.title;
  if (resolvedName) return resolvedName;
  const productType = entry.entityType === "PORTFOLIO" ? "Portfolio" : "Basket";
  return `${productType} ${entry.productId}`;
}

// Only self-directed products have a detail page. Managed rows, and rows whose
// catalog lookup has not resolved yet, render as plain text.
function allocationDetailPath(
  entry: InvestorStrategyRecord,
  allocationProducts: AllocationProductMap
): string | null {
  if (entry.entityType === "MANAGED_STRATEGY") return null;
  const product = allocationProducts[allocationKey(entry)];
  if (!product) return null;
  return `/alpha/discover/${entry.entityType.toLowerCase()}/${product.providerId}/${entry.productId}`;
}

async function hydrateAllocationProducts(
  entries: InvestorStrategyRecord[],
  signal: AbortSignal,
  onResolved: (key: string, product: AllocationProduct) => void
): Promise<void> {
  const uniqueEntries = new Map<
    string,
    { productType: "PORTFOLIO" | "BASKET"; productId: number }
  >();

  entries.forEach((entry) => {
    if (entry.entityType === "MANAGED_STRATEGY") return;
    uniqueEntries.set(allocationKey(entry), {
      productType: entry.entityType,
      productId: entry.productId,
    });
  });

  await Promise.all(
    Array.from(uniqueEntries.entries()).map(async ([key, entry]) => {
      try {
        const response = await productSearchService.searchProducts({
          page: 1,
          pageSize: 1,
          productType: entry.productType,
          productId: entry.productId,
        }, signal);
        const product = response.items.find((item) => (
          item.productType === entry.productType
          && item.productId === entry.productId
        ));
        if (product && !signal.aborted) {
          onResolved(key, { title: product.title, providerId: product.providerId });
        }
      } catch {
        // The allocation ID remains visible when optional name hydration fails.
      }
    })
  );
}

function SelfDashboardAdapter({
  progressResponses,
  effectiveAccountType,
  isPersistenceLoading,
  persistenceLoadFailed,
  onRetryPersistence,
  isReviewCompleted = false,
  isReviewCompletionLoading = false,
  reviewCompletionLoadFailed = false,
  onRetryReviewCompletion,
  requiresAccountTypeReview = false,
  setupV2ReadyOwner,
}: Extract<AlphaDashboardPreviewPageProps, { mode: "self" }>): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    getUserConfigValue,
    user,
    effectiveUserId,
    userConfigLoaded,
    userConfig,
  } = useAuth();
  const accountOwnerUserId = effectiveUserId === undefined
    ? user?.id
    : effectiveUserId ?? undefined;
  const [strategies, setStrategies] = useState<InvestorStrategyRecord[]>([]);
  const [allocationProducts, setAllocationProducts] = useState<AllocationProductMap>({});
  const [strategyLoadStatus, setStrategyLoadStatus] = useState<StrategyLoadStatus>("loading");
  const [strategyLoadAttempt, setStrategyLoadAttempt] = useState(0);
  const [isAllocationsModalOpen, setIsAllocationsModalOpen] = useState(false);
  // A What-If mix adopted from the alpha analyzer, waiting for the allocation
  // draft to finish loading. One-shot: consumed from route state (which is
  // stripped immediately) and cleared once applied or on a draft load error.
  const [pendingAdoptMix, setPendingAdoptMix] = useState<AlphaAdoptMixEntry[] | null>(null);
  // Raw context reader on purpose: no auto-init, so nothing loads unless an
  // adopt is pending or the editor opens (the modal triggers init itself).
  const allocationDraft = useAlphaAllocationDraftContext();

  // Consume an adopt handed over by the analyzer: open the editor, stash the
  // mix, and strip the state from history so back/refresh can never re-adopt.
  useEffect(() => {
    if (!hasAlphaAdoptMixRouteState(location.state)) return;
    if (effectiveAccountType === null) return;
    const adoptEntries = getAlphaAdoptMixFromRouteState(location.state, effectiveUserId);
    if (adoptEntries) {
      const accountAwareEntries = adoptEntries.length === 1
        && adoptEntries[0].entityType === "MANAGED_STRATEGY"
        ? [{
          ...adoptEntries[0],
          weight: getManagedStrategySeedWeight(effectiveAccountType === "retirement"),
        }]
        : adoptEntries;
      setPendingAdoptMix(accountAwareEntries);
      setIsAllocationsModalOpen(true);
    }
    navigate(location.pathname, {
      replace: true,
      state: stripAlphaAdoptMixRouteState(location.state),
    });
  }, [effectiveAccountType, effectiveUserId, location.pathname, location.state, navigate]);

  // Apply the pending adopt once the draft is loaded (the open modal triggers
  // the load; an already-loaded draft applies immediately). A load error
  // discards the adopt so it cannot surprise-apply after a later reload.
  const { status: allocationDraftStatus, adoptMix: applyAdoptMix } = allocationDraft;
  useEffect(() => {
    if (!pendingAdoptMix) return;
    if (allocationDraftStatus === "error") {
      setPendingAdoptMix(null);
      return;
    }
    if (allocationDraftStatus !== "loaded") return;
    applyAdoptMix(pendingAdoptMix);
    setPendingAdoptMix(null);
  }, [pendingAdoptMix, allocationDraftStatus, applyAdoptMix]);
  const [managedData, setManagedData] = useState<AlphaManagedStrategyPerformanceData | null>(null);
  const [managedDataError, setManagedDataError] = useState<string | null>(null);
  const [managedDataAttempt, setManagedDataAttempt] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<V3TimeFilter>("Since inception");

  const isCustomer = userConfigLoaded && getUserConfigValue("iscustomer") === "true";
  const isBrokerageProvisioned = userConfigLoaded && (isCustomer || getUserConfigValue("isbeta") === "true");
  const launchDate = getUserConfigValue("launch_date");
  const showStrategyActualPerformance = isCustomer
    && userConfigLoaded
    && Boolean(userConfig?.ibkrAccountId);
  const customerPerformance = useAlphaStrategyActualPerformance({
    enabled: isCustomer,
    includeActual: showStrategyActualPerformance,
    authenticatedUserId: accountOwnerUserId,
    launchDate,
    selectedKey: ACTUAL_COMPARISON_ALL_KEY,
  });
  const customerStrategyChart = customerPerformance.chart;

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setStrategyLoadStatus("loading");
    setAllocationProducts({});
    void investorStrategyService.getStrategyRecords().then((currentStrategies) => {
      if (!active) return;
      setStrategies(currentStrategies);
      setStrategyLoadStatus("loaded");

      void hydrateAllocationProducts(
        currentStrategies,
        controller.signal,
        (key, product) => {
          if (!active) return;
          setAllocationProducts((current) => ({ ...current, [key]: product }));
        }
      );
    }).catch(() => {
      if (active) setStrategyLoadStatus("failed");
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [strategyLoadAttempt]);

  const managedStrategies = strategies.filter(
    (strategy) => strategy.entityType === "MANAGED_STRATEGY"
  );
  const managedStrategy = managedStrategies.length === 1
    ? managedStrategies[0]
    : undefined;
  const hasManagedStrategy = managedStrategies.length > 0;
  const hasSingleManagedStrategy = managedStrategy !== undefined;
  const hasMultipleManagedStrategies = managedStrategies.length > 1;
  // Fully self-directed: holds allocations but none are Plutus Managed. Switches
  // the Why-Managed teaser to a "you could be doing more" nudge.
  const isFullySelfDirected = strategies.length > 0 && !hasManagedStrategy;
  // 100% Plutus Managed: every saved row is managed. Cash is not a row.
  // Fail closed until strategies load so Adopt cannot flash on a managed book.
  const isFullyManaged = strategyLoadStatus === "loaded"
    && strategies.length > 0
    && strategies.every((strategy) => strategy.entityType === "MANAGED_STRATEGY");
  const showManagedAdopt = strategyLoadStatus === "loaded"
    && !isFullyManaged
    && effectiveAccountType !== null;

  // Same one-shot seed as the editor's "Go fully managed": open the editor at
  // the account seed weight (100, or 95 for retirement; never margin) and
  // leave Apply to the investor.
  const handleAdoptManaged = (managedProductId: number) => {
    if (effectiveAccountType === null) return;
    setPendingAdoptMix([{
      entityType: "MANAGED_STRATEGY",
      productId: managedProductId,
      weight: getManagedStrategySeedWeight(effectiveAccountType === "retirement"),
      displayName: PLUTUS_MANAGED_LABEL,
    }]);
    setIsAllocationsModalOpen(true);
  };

  const routeStatus = routeCustomerStatus(location.state);
  const inferredCustomerStatus: CustomerStatus | null = strategyLoadStatus === "loaded"
    ? strategies.length > 0 ? "existing" : "new"
    : null;
  const customerStatus = routeStatus ?? inferredCustomerStatus;
  const isVerifiedSetupV2 = Boolean(setupV2ReadyOwner && setupV2ReadyOwner === user?.id
    && setupV2ReadyOwner === effectiveUserId && hasAlphaRoleAccess(user?.roles ?? []));
  const hasCompletePreReviewResponses = isVerifiedSetupV2 || progressResponses !== null
    && getAlphaSetupDestination(progressResponses, { hasCompletedReview: true }) === null;
  const reviewCompleted = isReviewCompleted;
  const canRenderDashboard = isBrokerageProvisioned && !requiresAccountTypeReview
    ? true
    : reviewCompleted && hasCompletePreReviewResponses;
  const canFetchAccountSummary = isCustomer;
  const accountSummaryQuery = useAccountSummary(undefined, canFetchAccountSummary);
  const portfolioValue = canFetchAccountSummary
    && !accountSummaryQuery.error
    && Number.isFinite(Number(accountSummaryQuery.data?.netLiquidationSecurity))
    ? Number(accountSummaryQuery.data?.netLiquidationSecurity)
    : null;
  const setupDestination = !isVerifiedSetupV2 && progressResponses
    ? getAlphaSetupDestination(progressResponses, { hasCompletedReview: reviewCompleted })
    : null;
  const canLoadManagedData = strategyLoadStatus === "loaded"
    && hasSingleManagedStrategy
    && !isCustomer
    && progressResponses !== null
    && setupDestination === null;

  useEffect(() => {
    if (!managedStrategy || !canLoadManagedData) {
      setManagedData(null);
      setManagedDataError(null);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setManagedData(null);
    setManagedDataError(null);
    void loadAlphaManagedStrategyDataByProductId(
      managedStrategy.productId,
      controller.signal
    ).then((data) => {
      if (active) setManagedData(data);
    }).catch((error: unknown) => {
      if (!active || controller.signal.aborted) return;
      setManagedDataError(
        error instanceof Error
          ? error.message
          : "We could not load this managed strategy."
      );
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [canLoadManagedData, managedDataAttempt, managedStrategy]);

  const chartAnchor = useMemo(() => {
    if (!managedData) return null;
    return new Date(managedData.window.endMs);
  }, [managedData]);
  const v3IntrinsicAvailability = useMemo(() => {
    if (!managedData) return emptyPeriodRecord(false);
    return emptyPeriodRecord(
      managedData.strategyFactors.length > 0 && managedData.benchmarkFactors.length > 0
    );
  }, [managedData]);
  const v3LoadedCoverage = useMemo(() => {
    if (!managedData || !chartAnchor) return emptyPeriodRecord(false);
    const earliestStrategyMs = new Date(managedData.strategyFactors[0]?.t ?? "").getTime();
    const earliestBenchmarkMs = new Date(managedData.benchmarkFactors[0]?.t ?? "").getTime();
    const earliestCoveredMs = Math.max(earliestStrategyMs, earliestBenchmarkMs)
      - V3_FACTOR_SLOT_MS;
    return Object.fromEntries(CHART_FILTERS.map((period) => {
      if (period === "All" || period === "Since inception") return [period, true];
      const start = getV3FilterWindowBounds(period, chartAnchor).start;
      return [period, Boolean(start) && earliestCoveredMs <= (start?.getTime() ?? 0)];
    })) as Record<V3TimeFilter, boolean>;
  }, [chartAnchor, managedData]);
  const v3HasDataByPeriod = useMemo(() => {
    if (!managedData || !chartAnchor) return emptyPeriodRecord(false);
    return Object.fromEntries(CHART_FILTERS.map((period) => [
      period,
      filterFactorsForPeriod(
        managedData.strategyFactors,
        period,
        chartAnchor,
        managedData.window.startMs
      ).length > 0
      && filterFactorsForPeriod(
        managedData.benchmarkFactors,
        period,
        chartAnchor,
        managedData.window.startMs
      ).length > 0,
    ])) as Record<V3TimeFilter, boolean>;
  }, [chartAnchor, managedData]);
  const availableFilters = CHART_FILTERS;
  const enabledFilters = useMemo(() => CHART_FILTERS.filter((period) => (
    v3IntrinsicAvailability[period]
    && v3LoadedCoverage[period]
    && v3HasDataByPeriod[period]
  )), [v3HasDataByPeriod, v3IntrinsicAvailability, v3LoadedCoverage]);

  useEffect(() => {
    if (enabledFilters.length === 0 || enabledFilters.includes(selectedPeriod)) return;
    setSelectedPeriod(getV3DefaultFilter(enabledFilters));
  }, [enabledFilters, selectedPeriod]);

  const strategySeries = useMemo(() => (
    managedData && chartAnchor
      ? buildPeriodSeries(
        managedData,
        selectedPeriod,
        managedData.strategyFactors,
        chartAnchor
      )
      : []
  ), [chartAnchor, managedData, selectedPeriod]);
  const benchmarkSeries = useMemo(() => (
    managedData && chartAnchor
      ? buildPeriodSeries(
        managedData,
        selectedPeriod,
        managedData.benchmarkFactors,
        chartAnchor
      )
      : []
  ), [chartAnchor, managedData, selectedPeriod]);
  const managedSelectedReturn = strategySeries.length > 0
    ? strategySeries[strategySeries.length - 1].performance
    : null;
  const managedDetailChart = useMemo(() => ({
    loading: (
      strategyLoadStatus === "loading"
      && reviewCompleted
      && !isCustomer
    ) || (
      managedData === null
      && managedDataError === null
      && canLoadManagedData
    ),
    error: managedDataError ?? (
      strategyLoadStatus === "failed" && reviewCompleted && !isCustomer
        ? "We could not load your saved strategy."
        : null
    ),
    chartHeight: 390,
    chartData: strategySeries,
    selectedPeriod,
    entityType: "MANAGED_STRATEGY" as const,
  }), [
    canLoadManagedData,
    isCustomer,
    managedData,
    managedDataError,
    reviewCompleted,
    selectedPeriod,
    strategyLoadStatus,
    strategySeries,
  ]);
  const selectedReturn = isCustomer
    ? customerStrategyChart.strategyTimeframePerformance[customerStrategyChart.selectedPeriod] ?? null
    : managedSelectedReturn;
  const providedBenchmarkSeries = useMemo(() => (
    ALPHA_BENCHMARK
      ? [{ benchmark: ALPHA_BENCHMARK, points: benchmarkSeries }]
      : []
  ), [benchmarkSeries]);

  const allocations = strategies;
  const allocationTotals = useMemo(
    () => computeStrategyTotals(allocations, ALPHA_ALLOCATION_BASIS_PCT, null),
    [allocations]
  );
  // Cash absorbs whatever is unallocated. A portfolio at or beyond the basis
  // holds no cash, so the sleeve floors at zero rather than going negative.
  const cashPct = Math.max(0, allocationTotals.remainingCurrentPct);
  const totalWithCashPct = allocationTotals.currentPct + cashPct;
  // Update opens the questionnaire in profile-update mode: preference and risk
  // questions only, returning here on save/back. Never the setup flow, so an
  // existing customer is never routed into review or agreement signing. Go to
  // setup sends a prospect into the flow itself; a customer with missing
  // answers also goes straight to the questions (they already have a live
  // account, so review/agreement/brokerage must never re-run for them).
  const openRiskProfileFromDashboard = () => {
    openAlphaRiskProfileUpdate(navigate, location.state,
      isVerifiedSetupV2 && !isCustomer && getUserConfigValue("isbeta") !== "true" ? "/alpha/setupv2/dashboard" : "/alpha/dashboard");
  };

  const startSetupFromDashboard = () => {
    const destination = setupDestination ?? { path: "/alpha" as const };
    navigate(destination.path, { state: location.state });
  };

  const showLoading = !canRenderDashboard
    && (isPersistenceLoading || isReviewCompletionLoading);
  if (showLoading) {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-[1180px] items-center justify-center px-4 py-16">
        <div role="status" className="flex items-center gap-3 text-sm text-v7-ink-soft">
          <LoaderCircle className="h-5 w-5 animate-spin text-v7-bronze-dark" aria-hidden="true" />
          Loading your strategy
        </div>
      </div>
    );
  }

  if (
    !canRenderDashboard
    && reviewCompletionLoadFailed
    && hasCompletePreReviewResponses
  ) {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-[760px] items-center px-4 py-16">
        <section role="alert" className="w-full rounded-v7-panel border border-red-200 bg-red-50 p-6 text-center">
          <CircleAlert className="mx-auto h-7 w-7 text-red-700" aria-hidden="true" />
          <h1 className="mt-3 font-v7-serif text-3xl font-normal text-v7-ink">
            We could not verify your signed agreement
          </h1>
          <p className="mt-2 text-sm text-red-700">Try checking your completed Review again.</p>
          <button
            type="button"
            onClick={() => onRetryReviewCompletion?.()}
            className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Retry verification
          </button>
        </section>
      </div>
    );
  }

  if (!canRenderDashboard && persistenceLoadFailed) {
    return (
      <div className="mx-auto flex min-h-full w-full max-w-[760px] items-center px-4 py-16">
        <section role="alert" className="w-full rounded-v7-panel border border-red-200 bg-red-50 p-6 text-center">
          <CircleAlert className="mx-auto h-7 w-7 text-red-700" aria-hidden="true" />
          <h1 className="mt-3 font-v7-serif text-3xl font-normal text-v7-ink">
            We could not load your saved setup
          </h1>
          <p className="mt-2 text-sm text-red-700">Try loading your setup details again.</p>
          <button
            type="button"
            onClick={onRetryPersistence}
            className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try again
          </button>
        </section>
      </div>
    );
  }

  if (!canRenderDashboard && (!progressResponses || setupDestination)) {
    const destination = setupDestination ?? { path: "/alpha" as const, label: "Start setup" };
    return (
      <div className="mx-auto flex min-h-full w-full max-w-[760px] items-center px-4 py-16">
        <section className="w-full border-y border-v7-ink/15 py-10 text-center">
          <h1 className="font-v7-serif text-4xl font-normal tracking-[-0.035em] text-v7-ink">
            We need more information
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-v7-ink-soft">
            Complete the next setup step so we can build your Plutus Managed strategy.
          </p>
          <button
            type="button"
            onClick={() => navigate(destination.path, { state: location.state })}
            className="mt-6 inline-flex min-h-12 items-center justify-center rounded-v7-control bg-v7-ink px-5 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft"
          >
            {destination.label}
          </button>
        </section>
      </div>
    );
  }

  return (
    <main
      data-testid="alpha-dashboard-content"
      data-customer-status={customerStatus ?? undefined}
      className="mx-auto min-h-full w-full max-w-[1180px] px-4 pb-28 pt-7 md:px-10 md:py-10"
    >
      <section className="overflow-hidden rounded-v7-panel border border-v7-ink/20 bg-v7-white" aria-labelledby="strategy-performance-heading">
        <div className="flex flex-wrap items-end justify-between gap-5 border-b border-v7-ink/15 px-5 py-5 md:px-7">
          <div>
            <p id="strategy-performance-heading" className="text-[10px] font-medium uppercase tracking-[0.1em] text-v7-ink/60">
              Portfolio value
            </p>
            <div className="mt-2 flex items-baseline gap-3">
              <span className="ph-mask font-v7-serif text-[38px] leading-none tracking-[-0.04em] text-v7-ink md:text-[46px]">
                {portfolioValue === null ? "--" : formatCurrencyWithCommas(portfolioValue, 0)}
              </span>
              {!isCustomer && selectedReturn !== null && (
                <span className={`font-mono text-sm font-medium ${selectedReturn >= 0 ? "text-v7-success" : "text-v7-rust"}`}>
                  {formatSignedPercentage(selectedReturn, 1)}
                </span>
              )}
            </div>
          </div>
          {isCustomer ? (
            <>
              <dl className="flex flex-1 items-end justify-start gap-6 sm:justify-center">
                {[
                  { label: "Since inception", period: "Since inception" },
                  { label: "Today", period: "Today" },
                ].map((metric) => {
                  const value = customerStrategyChart.strategyTimeframePerformance[metric.period] ?? null;
                  return (
                    <div key={metric.period}>
                      <dt className="text-[10px] font-semibold uppercase tracking-[0.1em] text-v7-ink/55">{metric.label}</dt>
                      <dd className={`mt-1 font-v7-serif text-lg ${value === null || value === 0 ? "text-v7-ink-soft" : value > 0 ? "text-v7-success" : "text-v7-rust"}`}>
                        {value === null ? "--" : formatSignedPercentage(value)}
                      </dd>
                    </div>
                  );
                })}
              </dl>
              <button
                type="button"
                data-testid="alpha-performance-header-button"
                onClick={() => navigate("/alpha/performance", { state: location.state })}
                className="ml-auto inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-v7-control bg-v7-ink px-4 text-[13px] font-medium text-v7-white shadow-md shadow-v7-ink/20 transition-all hover:bg-v7-ink-soft hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
              >
                Performance
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          ) : hasSingleManagedStrategy && (
            <p className="max-w-[360px] text-right text-xs leading-5 text-v7-ink/60">
              Plutus Managed model performance compared with the S&amp;P 500.
            </p>
          )}
        </div>

        {isCustomer ? (
          <div data-testid="alpha-strategy-performance-chart" className="px-3 pb-5 pt-4 md:px-5">
            {customerPerformance.actualError && (
              <div role="status" className="mb-4 flex items-center justify-between gap-3 rounded-v7-panel border border-v7-bronze/30 bg-v7-bronze/10 px-4 py-3 text-xs text-v7-bronze-dark">
                <span>{customerPerformance.actualError} Strategy performance remains available.</span>
                <button
                  type="button"
                  onClick={customerPerformance.retryActual}
                  disabled={customerPerformance.isActualFetching}
                  className="inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-v7-control px-2 font-medium hover:bg-v7-white disabled:cursor-wait disabled:opacity-60"
                >
                  {customerPerformance.isActualFetching ? (
                    <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {customerPerformance.isActualFetching ? "Retrying" : "Retry"}
                </button>
              </div>
            )}
            <AlphaPerformanceChartContent
              {...customerStrategyChart}
              chartHeight={390}
              isDarkMode={false}
              onRetry={customerPerformance.retry}
              showUnavailablePeriods
            />
          </div>
        ) : hasMultipleManagedStrategies ? (
          <div
            data-testid="alpha-managed-detail-unavailable"
            role="status"
            className="px-5 py-12 text-center"
          >
            <p className="text-sm text-v7-ink-soft">
              Managed strategy details are unavailable because this account has multiple managed strategies.
            </p>
          </div>
        ) : hasSingleManagedStrategy && (
          managedDataError ? (
            <div className="px-5 py-12 text-center" role="alert">
              <p className="text-sm text-red-700">{managedDataError}</p>
              <button
                type="button"
                onClick={() => setManagedDataAttempt((current) => current + 1)}
                className="mt-4 text-sm font-medium text-v7-bronze-dark underline underline-offset-4"
              >
                Retry performance
              </button>
            </div>
          ) : (
            <div className="px-3 pb-5 pt-3 md:px-5">
              <DetailNetBenchmarkChart
                chart={managedDetailChart}
                benchmarks={ALPHA_BENCHMARK ? [ALPHA_BENCHMARK] : []}
                providedBenchmarkSeries={providedBenchmarkSeries}
                compact
                showNet={false}
                showPeriodFilters={false}
                showWatermark={false}
                isDarkMode={false}
                useUtcDateLabels
                productLabel="Plutus Managed"
                ariaLabel="Plutus Managed model performance compared with the S&P 500"
              />
              <V3PeriodFilterRow
                timeFilterOptions={V3_INVESTOR_TIME_FILTER_OPTIONS}
                availableFilters={availableFilters}
                selectedPeriod={selectedPeriod}
                handleSelectPeriod={(period) => setSelectedPeriod(period as V3TimeFilter)}
                v3IntrinsicAvailability={v3IntrinsicAvailability}
                v3LoadedCoverage={v3LoadedCoverage}
                v3HasDataByPeriod={v3HasDataByPeriod}
                isDarkMode={false}
                appearance="alpha"
              />
            </div>
          )
        )}
      </section>

      <section className="mt-7 grid gap-7 border-y border-v7-ink/15 py-7 md:grid-cols-2" aria-label="Strategy details">
        <div>
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-v7-serif text-2xl font-normal text-v7-ink">Allocations</h2>
            <div className="flex items-center gap-3">
              {allocations.length > 0 && (
                <p className="text-xs text-v7-ink/50">
                  {allocations.length} {allocations.length === 1 ? "allocation" : "allocations"}
                </p>
              )}
              {strategyLoadStatus === "loaded" && allocations.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsAllocationsModalOpen(true)}
                  className="inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-v7-control border border-v7-ink/25 bg-v7-white px-3 text-xs font-semibold text-v7-ink transition-colors hover:border-v7-ink"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5 text-v7-bronze-dark" aria-hidden="true" />
                  Edit allocations
                </button>
              )}
            </div>
          </div>
          <div className="mt-4 divide-y divide-v7-ink/15 border-y border-v7-ink/15">
            {strategyLoadStatus === "loading" ? (
              <p role="status" className="py-4 text-sm text-v7-ink-soft">
                Loading allocations
              </p>
            ) : strategyLoadStatus === "failed" ? (
              <div className="flex items-center justify-between gap-4 py-4">
                <p role="alert" className="text-sm text-red-700">
                  Allocations are temporarily unavailable.
                </p>
                <button
                  type="button"
                  onClick={() => setStrategyLoadAttempt((current) => current + 1)}
                  className="min-h-10 shrink-0 rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 text-xs font-medium text-v7-ink transition-colors hover:border-v7-ink"
                >
                  Retry allocations
                </button>
              </div>
            ) : allocations.length === 0 ? (
              <div className="flex flex-col items-start gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-v7-ink-soft">You have no allocations yet.</p>
                <button
                  type="button"
                  data-testid="alpha-create-allocations"
                  onClick={() => setIsAllocationsModalOpen(true)}
                  className="inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-v7-control border border-v7-ink bg-v7-ink px-3 text-xs font-semibold text-v7-white transition-colors hover:bg-v7-ink-soft"
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
                  Create allocations
                </button>
              </div>
            ) : (
              <>
                {allocations.map((entry) => {
                  const label = allocationLabel(
                    entry,
                    allocationProducts,
                    hasMultipleManagedStrategies
                  );
                  const detailPath = allocationDetailPath(entry, allocationProducts);
                  const currentWeight = getCurrentWeight(entry);
                  const barWidth = Math.min(Math.abs(currentWeight), 100);
                  return (
                    <div
                      key={`${entry.entityType}-${entry.productId}`}
                      className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(90px,0.6fr)_auto]"
                    >
                      <p className="min-w-0 text-sm font-medium text-v7-ink">
                        {detailPath ? (
                          <Link
                            to={detailPath}
                            state={location.state}
                            data-testid={`allocation-link-${allocationKey(entry)}`}
                            className="underline underline-offset-4 transition-colors hover:text-v7-bronze-dark"
                          >
                            {label}
                          </Link>
                        ) : label}
                      </p>
                      <div
                        aria-hidden="true"
                        className="col-span-2 h-1.5 overflow-hidden rounded-full bg-v7-mist sm:col-span-1 sm:col-start-2 sm:row-start-1"
                      >
                        <div
                          data-testid={`allocation-bar-${allocationKey(entry)}`}
                          className="h-full rounded-full bg-v7-bronze"
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <span className="col-start-2 row-start-1 font-v7-serif text-base text-v7-ink sm:col-start-3">
                        {formatPercentage(currentWeight)}
                      </span>
                    </div>
                  );
                })}
                <div
                  data-testid="allocation-cash-row"
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(90px,0.6fr)_auto]"
                >
                  <p className="min-w-0 text-sm font-medium text-v7-ink-soft">
                    Cash
                  </p>
                  <div
                    aria-hidden="true"
                    className="col-span-2 h-1.5 overflow-hidden rounded-full bg-v7-mist sm:col-span-1 sm:col-start-2 sm:row-start-1"
                  >
                    <div
                      data-testid="allocation-bar-cash"
                      className="h-full rounded-full bg-v7-ink/25"
                      style={{ width: `${Math.min(cashPct, 100)}%` }}
                    />
                  </div>
                  <span className="col-start-2 row-start-1 font-v7-serif text-base text-v7-ink-soft sm:col-start-3">
                    {formatPercentage(cashPct)}
                  </span>
                </div>
                <div
                  data-testid="allocation-total-row"
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 py-4 sm:grid-cols-[minmax(0,1fr)_minmax(90px,0.6fr)_auto]"
                >
                  <p className="min-w-0 text-[10px] font-medium uppercase tracking-[0.08em] text-v7-ink/50">
                    Total
                  </p>
                  <span className="col-start-2 row-start-1 font-v7-serif text-base text-v7-ink sm:col-start-3">
                    {formatPercentage(totalWithCashPct)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <AlphaPreferencesCard
          progressResponses={progressResponses}
          onUpdate={openRiskProfileFromDashboard}
          onStartSetup={isBrokerageProvisioned ? openRiskProfileFromDashboard : startSetupFromDashboard}
        />
      </section>

      {hasMultipleManagedStrategies ? (
        <section
          data-testid="alpha-why-managed-unavailable"
          role="status"
          className="mt-6 rounded-v7-panel border border-v7-ink/15 bg-v7-paper px-4 py-4"
        >
          <p className="text-sm font-semibold text-v7-ink">Why Plutus Managed?</p>
          <p className="mt-1 text-[13px] text-v7-ink-soft">
            Details are unavailable because this account has multiple managed strategies.
          </p>
        </section>
      ) : (
        <AlphaWhyManagedTeaser
          productId={managedStrategy?.productId ?? null}
          selfDirected={isFullySelfDirected}
          showAdopt={showManagedAdopt}
          onAdopt={handleAdoptManaged}
        />
      )}

      <AlphaAllocationsModal
        open={isAllocationsModalOpen}
        effectiveAccountType={effectiveAccountType}
        onClose={() => setIsAllocationsModalOpen(false)}
        onSaved={() => setStrategyLoadAttempt((current) => current + 1)}
      />

    </main>
  );
}

export default function AlphaDashboardPreviewPage(
  props: AlphaDashboardPreviewPageProps
): React.ReactElement {
  return props.mode === "admin-target"
    ? <AdminTargetDashboardAdapter {...props} />
    : <SelfDashboardAdapter {...props} />;
}
